import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { getCompensationMethod } from "@redotech/redo-customer-portal-shared/return-review/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { isInternationalReturn } from "@redotech/redo-customer-portal-shared/util";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import {
  PendingReturnItem,
  ReturnProduct,
  VariantExchangeItem,
} from "@redotech/redo-model/draft-return/draft-return-items";
import {
  getReturnNonShippingProducts,
  getReturnShippingProducts,
  getVariantExchangeItemsAndProducts,
} from "@redotech/redo-model/draft-return/util";
import { Resource } from "@redotech/redo-model/localization/resource";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { CurrencyContext } from "@redotech/redo-web/currency";
import Keeping from "@redotech/redo-web/icon-old/keeping.svg";
import New from "@redotech/redo-web/icon-old/new.svg";
import ShipmentPrevious from "@redotech/redo-web/icon-old/shipment-previous.svg";
import { Big } from "big.js";
import * as classNames from "classnames";
import { memo, useContext } from "react";
import { adjustmentName } from "../../util/util";
import * as review from "../review.module.css";

export const InStoreReturnItems = memo(function InStoreReturnItems({
  draftReturn,
  settings,
  newOrderValue,
  returnValue,
}: {
  draftReturn: DraftReturn;
  settings: ReturnAppSettings;
  newOrderValue: number;
  returnValue: number;
}) {
  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "4px",
          alignItems: "center",
          paddingTop: "4px",
        }}
      >
        <div className={review.subheader}>Items you're returning</div>
      </div>
      <NonShippingItems
        draftReturn={draftReturn}
        newOrderValue={newOrderValue}
        returnValue={returnValue}
        settings={settings}
      />
    </>
  );
});

export const KeepingItems = memo(function KeepingItems({
  draftReturn,
  settings,
  newOrderValue,
  returnValue,
}: {
  draftReturn: DraftReturn;
  settings: ReturnAppSettings;
  newOrderValue: number;
  returnValue: number;
}) {
  const isInternational = isInternationalReturn(
    draftReturn.customer.shippingAddress?.country,
    draftReturn.shipments?.[0]?.address.country_name ?? undefined,
    settings,
  );

  const isBuyYourOwnLabel =
    settings.returns?.buyYourOwnLabel &&
    (isInternational || settings.returns?.byolDomestically);

  const sectionHeader = isBuyYourOwnLabel
    ? "Items you're sending back"
    : settings.theme.green_return_text
      ? settings.theme.green_return_text
      : draftReturn.returnType === ReturnTypeEnum.CLAIM
        ? "Items in this claim"
        : "Items you're keeping";
  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "4px",
          alignItems: "center",
          paddingTop: "4px",
        }}
      >
        <Keeping />
        <div className={review.subheader}>{sectionHeader}</div>
      </div>
      <NonShippingItems
        draftReturn={draftReturn}
        newOrderValue={newOrderValue}
        returnValue={returnValue}
        settings={settings}
      />
    </>
  );
});

const NonShippingItems = memo(function NonShippingItems({
  draftReturn,
  settings,
  newOrderValue,
  returnValue,
}: {
  draftReturn: DraftReturn;
  settings: ReturnAppSettings;
  newOrderValue: number;
  returnValue: number;
}) {
  const nonShippingItems = getReturnNonShippingProducts(draftReturn);
  return (
    <div className={review.cards}>
      {nonShippingItems.map(({ product, pendingItem }, index) => (
        <ProductCard
          draftReturn={draftReturn}
          isGreenReturn
          key={index}
          newOrderValue={newOrderValue}
          pendingReturnItem={pendingItem}
          product={product}
          returnType={draftReturn.returnType}
          returnValue={returnValue}
          settings={settings}
        />
      ))}
    </div>
  );
});

export const ReturnShippingItems = memo(function PreviousItems({
  draftReturn,
  settings,
  copyResource,
  newOrderValue,
  returnValue,
}: {
  draftReturn: DraftReturn;
  settings: ReturnAppSettings;
  copyResource: Resource;
  newOrderValue: number;
  returnValue: number;
}) {
  const products = getReturnShippingProducts(draftReturn);
  return (
    <>
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        <ShipmentPrevious />
        <div className={review.subheader}>{copyResource.ITEMS_OLD_HEADER}</div>
      </div>
      <div className={review.returningDisclaimer}>
        {copyResource.ITEMS_DISCLAIMER}
      </div>
      <div className={review.cards}>
        {products.map(({ product, pendingItem }, index) => (
          <ProductCard
            draftReturn={draftReturn}
            isGreenReturn={false}
            key={index}
            newOrderValue={newOrderValue}
            pendingReturnItem={pendingItem}
            product={product}
            returnType={draftReturn.returnType}
            returnValue={returnValue}
            settings={settings}
          />
        ))}
      </div>
    </>
  );
});

export const NewItems = memo(function NewItems({
  draftReturn,
  copyResource,
}: {
  draftReturn: DraftReturn;
  copyResource: Resource;
}) {
  const variantExchangeItems = getVariantExchangeItemsAndProducts(draftReturn);
  return (
    <div>
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        <New />
        <div className={review.subheader}>{copyResource.ITEMS_NEW_HEADER}</div>
      </div>
      <div className={review.cards}>
        {variantExchangeItems.map(({ variant, product, quantity }, index) => (
          <VariantExchangeItemCard
            key={index}
            product={product}
            quantity={quantity}
            variantExchangeItem={variant}
          />
        ))}
        {(draftReturn?.newOrderData?.items || []).map((item, index) => (
          <NewOrderItem item={item} key={index} />
        ))}
      </div>
    </div>
  );
});

const NewOrderItem = memo(function NewOrderItem({ item }: { item: any }) {
  const { formatCurrency } = useContext(CurrencyContext);
  return (
    <div className={review.card}>
      <div className={review.flex}>
        <img className={review.cardImage} src={item.images[0]} />
        <div className={review.cardItems}>
          <div className={review.cardItemTitle}>
            <div>{item.title}</div>
            {item.variantTitle !== "Default Title" ? (
              <div className={review.variantTitle}>{item.variantTitle}</div>
            ) : null}
            <div className={review.variantTitle}>Quantity: {item.quantity}</div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {Number(item.price) !== Number(item.itemValue) && (
              <div
                className={classNames(review.price, review.voided)}
                title={`Your discount of ${formatCurrency(
                  Number(item.price) - Number(item.itemValue),
                )} has been reapplied.`}
              >
                {formatCurrency(item.price)}
              </div>
            )}
            <div className={review.price}>
              {formatCurrency(parseFloat(item?.itemValue ?? "0"))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const VariantExchangeItemCard = memo(function VariantExchangeItemCard({
  product,
  variantExchangeItem,
  quantity,
}: {
  product: ReturnProduct | undefined;
  variantExchangeItem: VariantExchangeItem | undefined;
  quantity: number;
}) {
  const { formatCurrency } = useContext(CurrencyContext);

  if (!variantExchangeItem) {
    return null;
  }

  if (!product) {
    return null;
  }

  return (
    <div className={review.card}>
      <div className={review.flex}>
        <CardImage
          alt={product.variantData?.variants?.[0]?.title || ""}
          src={variantExchangeItem?.image ?? product?.image}
        />
        <div className={review.cardItems}>
          <div className={review.cardItemTitle}>
            <div className={review.title}>
              {variantExchangeItem.productTitle ?? product.title}
            </div>
            <div className={review.variantTitle}>
              {variantExchangeItem.title}
            </div>
            {quantity > 1 && (
              <div className={review.variantTitle}>Quantity: {quantity}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {product.price !== product.returnValue && (
              <div
                className={classNames(review.price, review.voided)}
                title={`Your discount of ${formatCurrency(
                  +product.price - +product.returnValue,
                )} has been reapplied.`}
              >
                {formatCurrency(product.price)}
              </div>
            )}
            <div className={review.price}>
              {formatCurrency(product.returnValue)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const ProductCard = memo(function ProductCard({
  draftReturn,
  pendingReturnItem,
  product,
  isGreenReturn,
  settings,
  returnType,
  newOrderValue,
  returnValue,
}: {
  draftReturn: DraftReturn;
  pendingReturnItem: PendingReturnItem;
  product: ReturnProduct | undefined;
  isGreenReturn: boolean;
  settings: ReturnAppSettings;
  returnType: string;
  newOrderValue: number;
  returnValue: number;
}) {
  const size = useScreenType();
  const { formatCurrency } = useContext(CurrencyContext);

  if (!product) {
    return null;
  }

  const returnQuantity = pendingReturnItem?.quantity ?? 1;

  return (
    <div className={review.card}>
      <div className={review.flex}>
        <CardImage
          alt={product.variantData?.variants?.[0]?.title || ""}
          src={product.image}
        />
        <div className={review.cardItems}>
          {size === ScreenType.MOBILE && (
            <Adjustment
              draftReturn={draftReturn}
              isGreenReturn={isGreenReturn}
              newOrderValue={newOrderValue}
              pendingReturnItem={pendingReturnItem}
              returnType={returnType}
              returnValue={returnValue}
              settings={settings}
            />
          )}
          <div className={review.cardItemTitle}>
            <div className={review.title}>{product?.title}</div>
            {product.variantTitle &&
            product.variantTitle !== "Default Title" ? (
              <div className={review.variantTitle}>
                {product.variantTitle.replaceAll("/", " | ")}
              </div>
            ) : null}
            {returnQuantity && returnQuantity > 1 && (
              <div className={review.variantTitle}>
                Quantity: {returnQuantity}
              </div>
            )}
          </div>
          {size === ScreenType.DESKTOP && (
            <Adjustment
              draftReturn={draftReturn}
              isGreenReturn={isGreenReturn}
              newOrderValue={newOrderValue}
              pendingReturnItem={pendingReturnItem}
              returnType={returnType}
              returnValue={returnValue}
              settings={settings}
            />
          )}
          <div style={{ display: "flex", gap: "8px" }}>
            {/* FIXME product.price is $0 if the item is from a previous exchange, so this will wrongly show that there's a discount */}
            {Number(product.price) !== Number(product.returnValue) && (
              <div className={classNames(review.price, review.voided)}>
                {formatCurrency(product.price)}
              </div>
            )}
            <div className={review.price}>
              {formatCurrency(product.returnValue)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const CardImage = memo(function CardImage(props: {
  src?: string;
  alt?: string;
}) {
  if (!props.src) {
    return null;
  }
  return <img alt={props.alt} className={review.cardImage} src={props.src} />;
});

const Adjustment = memo(function Adjustment({
  draftReturn,
  pendingReturnItem,
  settings,
  returnType,
  isGreenReturn,
  newOrderValue,
  returnValue,
}: {
  draftReturn: DraftReturn;
  pendingReturnItem: PendingReturnItem;
  settings: ReturnAppSettings;
  returnType: string;
  isGreenReturn: boolean;
  newOrderValue: number;
  returnValue: number;
}) {
  const { formatCurrency } = useContext(CurrencyContext);
  const compensationMethod = getCompensationMethod(draftReturn);
  const nonAccruableCreditAvailable =
    compensationMethod.itemValues?.[pendingReturnItem?.id]?.exchangeCredit;

  const valueDifference = new Big(newOrderValue).minus(returnValue);
  const usedNonAccruableCredit =
    valueDifference.lt(0) || nonAccruableCreditAvailable == null
      ? 0
      : valueDifference.gt(nonAccruableCreditAvailable)
        ? nonAccruableCreditAvailable.toNumber()
        : valueDifference.toNumber();

  const adjustment = (
    compensationMethod.adjustments[pendingReturnItem?.id] ?? new Big(0)
  )
    .div(pendingReturnItem?.quantity || 1)
    .plus(usedNonAccruableCredit ?? 0);

  const showAdjustment =
    returnType === ReturnTypeEnum.CLAIM ||
    returnType === ReturnTypeEnum.WARRANTY
      ? false
      : adjustment !== undefined && !adjustment.eq(0);

  const adjustmentValue =
    returnType === ReturnTypeEnum.CLAIM ? new Big(0) : adjustment;

  return showAdjustment ? (
    <div
      className={
        isGreenReturn
          ? review.greenReturnAdjustment
          : adjustmentValue.gt(0)
            ? review.adjustmentBonus
            : review.adjustmentFee
      }
    >
      <div>
        {isGreenReturn
          ? `${formatCurrency(adjustmentValue.toNumber())}`
          : `${adjustmentValue.gt(0) ? "+ " : ""}${formatCurrency(
              Math.abs(adjustmentValue.toNumber()),
            )} ${adjustmentName(settings.returnAdjustment, adjustmentValue.toNumber())}`}
      </div>
    </div>
  ) : null;
});
